import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { isEmpty, get, debounce } from "lodash";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import {
  chevronLeftIcon,
  errorIcon,
  menuDeleteIcon,
  menuDuplicateIcon,
  menuEditIcon,
  endpointIcon,
} from "../../assets/icons";
import { actions as drawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import { actions as dataModelActions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import {
  deleteEndpoint,
  selectEndpoints,
  selectSelectedDataModel,
  selectDataModelRegions,
  selectDeployStatus,
  setDeployStatus,
  setSelectedEndpoint,
  fetchServices,
  fetchPipelineStatus,
  selectCollections,
  selectTotalEndpoints,
  selectEndpointFilters,
} from "./index";
import { PopOver } from "../../components/atoms";
import {
  EmptyLayout,
  SearchBar,
  SuggestionBar,
  Table,
  Footer,
  Tooltip,
} from "../../components/molecules";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { getDataModelSuggestionMessage, getRegion } from "./services.helpers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import {
  POPOVER_BUTTONS,
  STATUS,
  TABS,
  FOOTER_CONSTANTS,
  DEPLOY_STATUSES,
  REFRESH_DEPLOY_STATUS,
  TIME_TO_ALLOW_REDEPLOY,
  EMPTY_STRING,
  SEARCH_DEBOUNCE_TIME,
  ZERO,
} from "../../constants";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { getDeploymentStatus, renderNoDataModelLayout } from "../../helpers";
import { ReDeployButton } from "../../components/molecules/index";
import { selectDataModelDeploymentsTrack } from "./dataModels.selectors";

const { MANAGE_ENDPOINT_DRAWER, DEPLOYMENT_LOGS_DRAWER } = DRAWERS;
const { ENDPOINT_TEMPLATE_MODAL } = MODALS;
const { DELETE, DUPLICATE, EDIT } = POPOVER_BUTTONS;
const { INACTIVE } = STATUS;
const { SETTINGS } = TABS;
const NAVIGATE_BACK = -1;

const { setSelectedDataModelId, setEndpointFilters } = dataModelActions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedDrawer } = drawerActions;
const { setSelectedTab, setSelectedSubTab } = breadCrumbActions;
const { ENDPOINTS_TITLE, ENDPOINTS_SUBTITLE } = FOOTER_CONSTANTS;
const { DELETE_ENDPOINT, FETCH_ENDPOINTS, FETCH_COLLECTIONS } = SPINNER_NAMES;
const { DELETE_MODAL } = MODALS;
const {
  SUCCESS,
  FAILURE,
  IN_PROGRESS,
  CREATING_PROJECT,
  REPO_NOT_FOUND,
  CANCELLED,
} = DEPLOY_STATUSES;
let pipelineStatusInterval;
const ENDPOINTS = "endpoints";
const ENDPOINT_DEPLOY_AT = "endpointsDeployAt";

const Endpoints = () => {
  const { dataModelId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allEndpoints = useSelector(selectEndpoints);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const dataModelRegions = useSelector(selectDataModelRegions);
  const deployStatus = useSelector(selectDeployStatus);
  const collections = useSelector(selectCollections);
  const { deploymentStatus: deploymentLogs } = useSelector(
    selectDataModelDeploymentsTrack
  );
  const totalEndpoints = useSelector(selectTotalEndpoints);
  const endpointFilters = useSelector(selectEndpointFilters);

  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [shouldShowSuggestionBar, setShouldShowSuggestionBar] = useState(false);
  const [hasInvalidEndpoint, setHasInvalidEndpoint] = useState(true);
  const [hasEmptyEndpointsAndCollections, setHasEmptyEndpointsAndCollections] =
    useState(true);
  const [hasEmptyEndpoints, setHasEmptyEndpoints] = useState(true);
  const [deployedAt, setDeployedAt] = useState(ZERO);
  const [timerActive, setTimerActive] = useState(true);
  const [show, setShow] = useState(false);
  const endpointsDeployedTime = JSON.parse(
    localStorage.getItem(ENDPOINT_DEPLOY_AT)
  );

  useEffect(() => {
    const deploymentTime =
      endpointsDeployedTime &&
      selectedDataModel &&
      endpointsDeployedTime[selectedDataModel?.id];
    deploymentTime && setDeployedAt(deploymentTime);
  }, [selectedDataModel, endpointsDeployedTime]);

  useEffect(() => {
    const timerInterval = setInterval(
      () => setTimerActive(+deployedAt + TIME_TO_ALLOW_REDEPLOY > Date.now()),
      1000
    );
    return () => clearInterval(timerInterval);
  }, [deployedAt]);

  useEffect(() => {
    if (selectedDataModel && allEndpoints)
      setHasInvalidEndpoint(allEndpoints.some(({ isValid }) => !isValid));
  }, [allEndpoints, selectedDataModel]);

  useEffect(() => {
    if (selectedDataModel) setHasEmptyEndpoints(isEmpty(allEndpoints));
  }, [selectedDataModel, allEndpoints]);

  const handleRefresh = useCallback(() => {
    if (!selectedDataModel) return;
    const { id, dbRefName, cloudService } = selectedDataModel;
    dispatch(
      fetchPipelineStatus({
        repoName: dbRefName,
        dataModelId: id,
        cloudService,
      })
    ).then((res) => {
      if (res.payload?.backend) {
        const status = getDeploymentStatus(res.payload.backend);
        const updatedDeploymentsTime = {
          ...endpointsDeployedTime,
          [id]: Date.now(),
        };
        if (status === SUCCESS)
          localStorage.setItem(
            ENDPOINT_DEPLOY_AT,
            JSON.stringify(updatedDeploymentsTime)
          );

        if ([SUCCESS, FAILURE, REPO_NOT_FOUND, CANCELLED].includes(status)) {
          dispatch(setDeployStatus(status));
          clearInterval(pipelineStatusInterval);
        }
      }
    });
  }, [dispatch, selectedDataModel, endpointsDeployedTime]);

  useEffect(() => {
    if (deployStatus === IN_PROGRESS)
      pipelineStatusInterval = setInterval(
        handleRefresh,
        REFRESH_DEPLOY_STATUS
      );
    return () => clearInterval(pipelineStatusInterval);
  }, [dispatch, handleRefresh, deployStatus]);

  useEffect(() => {
    dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId]);

  useEffect(() => {
    if (selectedDataModel) {
      const showSuggestionBar = Boolean(
        selectedDataModel.status === INACTIVE ||
          (dataModelRegions &&
            selectedDataModel.region !== getRegion(dataModelRegions))
      );
      setShouldShowSuggestionBar(showSuggestionBar);
      dispatch(setSelectedTab(selectedDataModel));
    }
  }, [dispatch, selectedDataModel, dataModelRegions]);

  useEffect(() => {
    return () => dispatch(setEndpointFilters());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDataModel)
      setHasEmptyEndpointsAndCollections(
        isEmpty(collections.data) || isEmpty(allEndpoints)
      );
  }, [selectedDataModel, allEndpoints, collections]);

  const popOverOptions = (row) => [
    {
      name: EDIT,
      icon: menuEditIcon,
      handleOnClick: () => handleOnEditEndpoint(row),
    },
    {
      name: DUPLICATE,
      icon: menuDuplicateIcon,
      handleOnClick: () => console.log("Clicked duplicate"),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(DELETE_MODAL));
        dispatch(setItemToBeDeleted(row));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  const buttonFormatter = (row) => (
    <div onClick={(e) => e.stopPropagation()}>
      <PopOver
        options={popOverOptions(row)}
        classes="right-6 -bottom-3"
        data={row}
        headerClasses="flex justify-end"
      />
    </div>
  );

  const nameFormatter = ({ name, isValid }) => (
    <div className="flex justify-between">
      <p>{name}</p>
      <img
        alt="error"
        className={classNames("w-4", {
          hidden: isValid,
        })}
        src={errorIcon}
      />
    </div>
  );

  const serviceFormatter = (row) => (
    <Link
      to={`/data-models/${dataModelId}/services/${row.serviceId}/details`}
      className="text-indigo-200 hover:text-indigo-500"
      onClick={(e) => {
        dispatch(setSelectedSubTab(ENDPOINTS));
        e.stopPropagation();
      }}
    >
      {get(row, "services.name", EMPTY_STRING)}
    </Link>
  );

  const columns = [
    {
      text: "name",
      formatter: nameFormatter,
    },
    {
      dataField: "method",
      text: "Method",
    },
    {
      dataField: "url",
      text: "Url",
    },
    {
      text: "Service",
      formatter: serviceFormatter,
    },
    {
      width: 70,
      formatter: buttonFormatter,
    },
  ];

  const handleDeleteEndpoints = useCallback(
    (row) => {
      dispatch(deleteEndpoint(row.id)).then((res) => {
        if (res.payload) {
          dispatch(fetchServices({ id: dataModelId }));
          dispatch(setItemToBeDeleted(null));
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
        }
      });
    },
    [dispatch, dataModelId]
  );

  const getButtonTitle = () => {
    if (deployStatus === SUCCESS) return "Deployed";
    else if (deployStatus === FAILURE) return "Deployment Failed";
    else return "Deploying...";
  };

  const shouldShowDeployStatus = useCallback(
    () =>
      [SUCCESS, FAILURE, IN_PROGRESS, CREATING_PROJECT].includes(deployStatus),
    [deployStatus]
  );

  const shouldShowDeployButton = useCallback(
    () => ![IN_PROGRESS, CREATING_PROJECT].includes(deployStatus),
    [deployStatus]
  );

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedModal(ENDPOINT_TEMPLATE_MODAL)),
    [dispatch]
  );

  const handleOnEditEndpoint = (row) => {
    dispatch(setSelectedEndpoint(row));
    dispatch(setSelectedDrawer(MANAGE_ENDPOINT_DRAWER));
  };

  const handleOnSearch = debounce(
    (name) => dispatch(setEndpointFilters({ name })),
    SEARCH_DEBOUNCE_TIME
  );

  const renderEndpoints = () =>
    !isEmpty(allEndpoints) ? (
      <>
        <Table
          data={allEndpoints}
          columns={columns}
          className="Bordered"
          onRowClick={handleOnEditEndpoint}
        />
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteEndpoints}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Endpoint"
            spinner={DELETE_ENDPOINT}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Endpoint"
        subTitle="You can get started by creating a new endpoint."
        buttonText="Create New Endpoint"
        image={endpointIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );

  const isDeployBtnDisabled =
    hasEmptyEndpoints || hasInvalidEndpoint || !deployStatus || timerActive;

  return (
    <div className="content flex-1 flex flex-col overflow-auto">
      <Helmet>
        <title>Endpoints | TavaJS</title>
      </Helmet>
      <div className="flex-col xs:flex-row flex justify-between mb-4 gap-4  px-6 pt-4">
        <div className="flex flex-row ">
          <button
            className="bg-gray-800 px-2 h-10 rounded-md hover:bg-gray-700/60 mr-1"
            onClick={() => navigate(NAVIGATE_BACK)}
          >
            <img src={chevronLeftIcon} width="24" alt="chevron-left-icon" />
          </button>
          {!!totalEndpoints && (
            <SearchBar
              placeholder="Search"
              handleOnSearch={handleOnSearch}
              filters={endpointFilters}
            />
          )}
        </div>
        {selectedDataModel && (
          <div className="flex-col xs:flex-row flex gap-4">
            {shouldShowDeployButton() && (
              <ReDeployButton
                hasInvalidEndpoint={hasInvalidEndpoint}
                filteredEndpoints={allEndpoints}
                isDeployBtnDisabled={isDeployBtnDisabled}
                deployStatus={deployStatus}
                hasEmptyEndpointsAndCollections={
                  hasEmptyEndpointsAndCollections
                }
                deploymentLogs={deploymentLogs}
                deployedAt={deployedAt}
                isDataModelSection={true}
                setShow={setShow}
                show={show}
                handleOnSubmit={() => setShow(true)}
              />
            )}
            {shouldShowDeployStatus() && (
              <Tooltip message="Click here to check logs" shouldShow={true}>
                <button
                  type="button"
                  className={classNames(
                    "btn btn-primary w-full xs:w-auto h-auto",
                    {
                      "bg-yellow-300 text-black hover:bg-yellow-400 active:bg-yellow-300 disabled:hover:bg-yellow-200":
                        deployStatus === IN_PROGRESS ||
                        deployStatus === CREATING_PROJECT,
                      "bg-green-500 text-white hover:bg-green-600 active:bg-green-500 disabled:hover:bg-green-300":
                        deployStatus === SUCCESS,
                      "bg-red-500 text-white hover:bg-red-600 active:bg-red-500 disabled:hover:bg-red-300":
                        deployStatus === FAILURE,
                    }
                  )}
                  onClick={() =>
                    dispatch(setSelectedDrawer(DEPLOYMENT_LOGS_DRAWER))
                  }
                >
                  {getButtonTitle()}
                </button>
              </Tooltip>
            )}
            <div className="flex-col xs:flex-row flex gap-4">
              <button
                type="button"
                className="btn btn-primary w-full xs:w-auto h-auto"
                onClick={handleShouldShowDrawer}
              >
                Create Endpoint
              </button>
            </div>
          </div>
        )}
      </div>
      {selectedDataModel && shouldShowSuggestionBar && (
        <SuggestionBar
          text={getDataModelSuggestionMessage(
            selectedDataModel,
            dataModelRegions
          )}
          link={SETTINGS}
          handleOnClick={() => navigate(`/data-models/${dataModelId}/settings`)}
          classes="text-red-600"
        />
      )}
      <div className="scrollbar overflow-auto h-full w-full">
        <div className="justify-between flex flex-col flex-1 px-6 h-full">
          <Spinner name={[FETCH_ENDPOINTS, FETCH_COLLECTIONS]}>
            {selectedDataModel
              ? renderEndpoints()
              : renderNoDataModelLayout(navigate)}
          </Spinner>
          {selectedDataModel && (
            <Footer title={ENDPOINTS_TITLE} subtitle={ENDPOINTS_SUBTITLE} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Endpoints;
